import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import HarbourPortalShell from './HarbourPortalShell';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourCountPill from '../../components/harbour/HarbourCountPill';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourButton from '../../components/harbour/HarbourButton';
import { senBoardColumns, referralBoardColumns, senStoppedChips } from '../../utils/harbourPupils';
import '../../components/harbour/harbour.css';

/*
 * Pathway overview boards (TDI-53 sections 13 and 19): SEN runs Assess, Plan,
 * Do, Review; referral runs In progress to Closed. One card per pupil (SEN)
 * or per referral pathway (referral). Boards are SENCO desktop only.
 */
export default function HarbourBoardPage({ pathway }) {
  const navigate = useNavigate();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });

  const isSen = pathway === 'sen';
  const columns = isSen ? senBoardColumns(pupils) : referralBoardColumns(pupils);
  const userName = user?.displayName || user?.email || 'Account';

  return (
    <HarbourPortalShell userName={userName} userEmail={user?.email} userType={claims?.userType}>
      <div className="flex items-center gap-2">
        <HarbourPathwayMark pathway={isSen ? 'sen' : 'referral'} />
        <h1 className="harbour-heading-32">{isSen ? 'SEND pathway overview' : 'Referral pathway overview'}</h1>
      </div>

      {error ? (
        <div className="flex flex-col gap-4 max-w-[760px]">
          <HarbourNotice kind="error" title="We could not load this page right now">
            Check your connection, then try again.
          </HarbourNotice>
          <div>
            <HarbourButton kind="secondary" onClick={() => reload()}>Try again</HarbourButton>
          </div>
        </div>
      ) : loading && pupils.length === 0 ? (
        <div className={`harbour-board harbour-board--${isSen ? 'sen' : 'referral'}`}>
          <div className="harbour-skeleton-bars animate-pulse w-full" aria-hidden="true">
            <div className="harbour-skeleton" />
            <div className="harbour-skeleton" />
            <div className="harbour-skeleton" />
          </div>
        </div>
      ) : (
        <div className={`harbour-board harbour-board--${isSen ? 'sen' : 'referral'}`}>
          {columns.map((col) => (
            <div key={col.key} className="harbour-board__column">
              <div className="harbour-board__header">
                {col.label}
                <span className="ml-auto">
                  <HarbourCountPill count={col.cards.length} tone={col.cards.length === 0 ? 'neutralZero' : 'default'} />
                </span>
              </div>
              {col.cards.map((card) => (
                <div key={`${card.id}-${card.pathwayKey || 'sen'}`} className="harbour-board__card">
                  <span className="harbour-board__card-head">
                    <button type="button" className="harbour-board__card-name" onClick={() => navigate(`/rms/pupils/${card.id}`)}>
                      {card.name}
                    </button>
                    <HarbourIcon name="arrow-right" size={20} className="harbour-board__card-arrow" />
                  </span>
                  {card.yearGroup && <span className="harbour-board__card-meta">{card.yearGroup}</span>}
                  {isSen ? (
                    <SenCardRows pupil={pupils.find((p) => p.id === card.id)} />
                  ) : (
                    // The referral, then when it was sent or picked up
                    // (TDI-694, 3192:183326).
                    <span className="harbour-board__card-referral">
                      <span className="harbour-body-16-bold">{card.pathwayLabel}</span>
                      {card.dateLine && <span className="harbour-board__card-meta">{card.dateLine}</span>}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </HarbourPortalShell>
  );
}

/*
 * The board renders the same lane rows the pupil card does (buildSenLane), so a
 * pupil reads the same status in both places. Deriving the chips here a second
 * time is what let the board say "Report available" while the card said
 * "Plan created".
 */
function SenCardRows({ pupil }) {
  const sen = (pupil && pupil.sen) || null;
  // A stopped pathway has no rows, only the chip saying it stopped; without
  // it the card sat in the column it reached looking like it was still going.
  const rows = [
    ...((sen && sen.rows) || []),
    ...senStoppedChips(sen).map((chip) => ({ key: 'stopped', label: '', chip })),
  ];
  if (rows.length === 0) return null;
  return (
    <>
      {rows.map((row) => (
        <React.Fragment key={row.key}>
          {row.label ? <span className="harbour-board__card-row-label">{row.label}</span> : null}
          <span>
            <HarbourStatusChip status={row.chip.status} label={row.chip.label} tone={row.chip.tone} />
          </span>
        </React.Fragment>
      ))}
    </>
  );
}
